import { CheckCircleTwoTone, HomeOutlined, QuestionCircleTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { householdCareChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: 'Why should I care about household chores if someone else will do them?',
    answer: (
      <>
        Because one day you'll live alone and no one else will clean up after you. Keeping your personal space organized now builds the same
        habit of ownership described in <Link to="/adulting-101/responsibility">Responsibility</Link>.
      </>
    ),
  },
  {
    question: 'Why does it matter if I leave dirty clothes on the floor instead of in a hamper?',
    answer: (
      <>
        Clothes left on the floor get wrinkled, smell up the room, and make laundry day take longer since you have to hunt them down. A
        hamper is a five-second habit that saves you time later, similar to the small daily habits in{' '}
        <Link to="/adulting-101/time-management">Time Management</Link> that prevent bigger problems.
      </>
    ),
  },
  {
    question: 'How often do I need to do the dishes?',
    answer: (
      <>
        Right after each meal is best — food hardens and attracts pests the longer dishes sit. Doing a little bit consistently is easier
        than a giant pile later, the same principle behind avoiding procrastination in{' '}
        <Link to="/adulting-101/time-management">Time Management</Link>.
      </>
    ),
  },
  {
    question: "What if I don't know how to do laundry properly?",
    answer: (
      <>
        Ask for help or look it up — there are tutorials for everything, from sorting colors to reading care labels. Being willing to learn
        is more important than already knowing everything, the same attitude that helps with{' '}
        <Link to="/adulting-101/problem-solving">Problem Solving</Link> when you're stuck on something new.
      </>
    ),
  },
  {
    question: 'How often should I vacuum or sweep common areas?',
    answer: (
      <>
        Once or twice a week is usually enough for shared spaces, more often if people track in dirt or you have pets. Contributing to
        shared spaces is part of being considerate, which matters just as much in{' '}
        <Link to="/adulting-101/friends-and-family">Friends and Family</Link>.
      </>
    ),
  },
  {
    question: 'Is it really a big deal if I let the trash pile up?',
    answer: (
      <>
        Overflowing trash smells, attracts pests, and is an easy thing for others to notice and judge. Taking it out when it's full, without
        needing a reminder, is exactly the kind of follow-through discussed in <Link to="/adulting-101/responsibility">Responsibility</Link>
        .
      </>
    ),
  },
  {
    question: 'Why should I put things back where they belong instead of leaving clutter?',
    answer: (
      <>
        Clutter makes it harder to find things and creates visual stress, even if you don't notice it consciously. Keeping a tidy space can
        actually support the kind of calm, mindful headspace discussed in{' '}
        <Link to="/adulting-101/spiritual-awareness">Spiritual Awareness</Link>.
      </>
    ),
  },
  {
    question: 'How clean does the bathroom really need to be?',
    answer: (
      <>
        A dirty bathroom spreads germs and is one of the fastest ways to lose the trust of roommates or family. Wiping down surfaces weekly
        keeps it from becoming an overwhelming job, the same prevention strategy used in{' '}
        <Link to="/adulting-101/financial-literacy">Financial Literacy</Link> when you avoid letting small problems turn into big costs.
      </>
    ),
  },
  {
    question: 'Why should I report things that need fixing instead of ignoring them?',
    answer: (
      <>
        A small leak or loose hinge often turns into an expensive repair if ignored. Speaking up early shows the kind of clear{' '}
        <Link to="/adulting-101/communication-skills">Communication Skills</Link> that keeps small issues from becoming big ones.
      </>
    ),
  },
]

export function Adulting101HouseholdCarePage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Household Care</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #e6f4ff 0%, #ffffff 220px)' }}>
        <Title level={2}>A clean and organized home is a reflection of someone who takes care of their life.</Title>
        <Paragraph type="secondary">
          Household chores aren't just about cleaning — they're about responsibility, respect for shared spaces, and preparing yourself to
          live independently. Learning to maintain a home is a crucial adult skill.
        </Paragraph>

        <Card
          title={
            <Space>
              <HomeOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
              Household Care Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, your home is well maintained!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Household care is an ongoing responsibility. The more consistent you are, the easier it becomes, and the better your living
              space will be.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Household Care
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
